import * as React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";

import { apiFetch, ApiError } from "@/lib/api-client";
import { decodeJwt } from "@/lib/decode-jwt";
import { useAuth } from "@/contexts/auth-context";
import { canRoleAccessPath, pathnameOf } from "@/routes/route-access";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { Banner } from "@/components/ui/banner";
import { credentialsSchema, isOtpComplete, type CredentialsValues } from "@/routes/sign-in/sign-in-schema";
import { toAuthBanner, type AuthBanner } from "@/routes/sign-in/auth-error";

// Two-step sign-in inside a single auth card (UX Screen 01): credentials → email OTP.
// The card swaps its inner step; this is NOT a route change.
//
// Auth chain (LOCKED order, already-shipped backend — Stories 2.3–2.5):
//   1. POST /auth/login        { email, password } -> { mfa_token }
//   2. POST /auth/mfa/send     { mfa_token }        -> { resend_available_in_s }  (we send; login does not)
//   3. POST /auth/mfa/verify   { mfa_token, code }  -> { verified: true }
//   4. POST /auth/token        { mfa_token }        -> { access_token } (+ HttpOnly refresh cookie)
// Then setSession(access_token) -> navigate to the guard-captured return-to path
// (location.state.from) when present, internal, AND reachable by the new user's role,
// else "/", where HomeRedirect/ProtectedLayout route by role and honour
// force_password_change. We never re-derive routing here — the return-to path still
// passes back through the guards. The role check only stops a path stashed for a
// *different* identity (e.g. an admin who signed out on /admin/users) from bouncing a
// fresh clinician login straight to /403 (Franklin#317).
//
// Secrets (mfa_token, OTP code, access_token) live in component state only — never
// rendered, logged, or placed in URLs. All four calls set skipAuthRefresh: a 401
// from them is a real auth error (AUTH-001/AUTH-004), not a session to silently
// refresh.

type Step = "credentials" | "otp";

// Fallback cooldown (seconds) when the backend rate-limits a code request (AUTH-005)
// — keeps the resend control disabled even though we cannot read the Retry-After
// header through apiFetch.
const RATE_LIMIT_COOLDOWN_S = 60;

// Coerce a backend-supplied cooldown into a safe, non-negative whole number of
// seconds. Guards against contract drift (negative, NaN, or fractional values)
// that would otherwise enable resend instantly or render an odd countdown.
function toCountdownSeconds(value: unknown): number {
  const seconds = Math.floor(Number(value));
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
}

// Resolve a post-login return-to target from the guard-captured location.state.from.
// Only an internal, single-slash absolute path is honoured; protocol-relative ("//host")
// and backslash ("/\host") forms — which some browsers normalise to an external origin —
// and any non-string fall back to "/". Prevents an open-redirect via a crafted state.
function safeReturnTo(value: unknown): string {
  if (typeof value !== "string") return "/";
  if (!value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) return "/";
  return value;
}

interface LoginResponse {
  mfa_token: string;
  message: string;
}

interface SendResponse {
  message: string;
  resend_available_in_s: number;
}

interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

const HEDGED_REGISTERED_NOTICE =
  "If this email is not registered, check your inbox for sign-in details.";

export function SignInScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession } = useAuth();

  // One-time notice after registration. Snapshotted on mount so later state changes
  // (e.g. an auth error banner) don't make it flicker. Legacy sign-up passes the API
  // `message` as `registeredNotice` — new and duplicate addresses share a hedged
  // acknowledgement, so this screen must not invent "account created" / "credentials
  // emailed" copy. Story 2.11 still sends `{ registered: true }` with no notice;
  // that path uses the same hedged sentence rather than claiming an account exists.
  const [registeredNotice] = React.useState(() => {
    const state = location.state as
      | { registered?: boolean; registeredNotice?: unknown }
      | null;
    if (!state?.registered) return null;
    return typeof state.registeredNotice === "string" && state.registeredNotice
      ? state.registeredNotice
      : HEDGED_REGISTERED_NOTICE;
  });

  // Deep-link return-to: a route guard that bounced an unauthenticated user here stashed
  // their intended path in location.state.from (guards.tsx). Snapshot it on mount so the
  // registered-flag cleanup below (which resets state to null) can't drop it; post-login
  // we send them back there instead of the role landing page.
  const [returnTo] = React.useState(
    () => safeReturnTo((location.state as { from?: unknown } | null)?.from),
  );

  // Consume the flag once: drop it from this history entry so a browser reload (or a
  // back/forward into it) does not resurface the post-registration banner. The lazy
  // snapshot above keeps the banner visible for this mount regardless.
  React.useEffect(() => {
    if ((location.state as { registered?: boolean } | null)?.registered) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const [step, setStep] = React.useState<Step>("credentials");
  const [banner, setBanner] = React.useState<AuthBanner | null>(null);
  const [code, setCode] = React.useState("");
  const [resendInSeconds, setResendInSeconds] = React.useState(0);

  // Secret: held in a ref so a re-render can never paint it and it is not React state
  // that could leak through devtools props. Cleared on Back.
  const mfaTokenRef = React.useRef<string | null>(null);

  // Synchronous double-submit guard. `isPending`/`disabled` both flip on a later
  // render, so a same-tick double-click can fire /auth/login twice before either
  // updates — counting toward AUTH-002 lockout and sending two OTP emails. This ref
  // blocks the second call in the same tick; it is released when login settles.
  const loginInFlightRef = React.useRef(false);

  const form = useForm<CredentialsValues>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: "", password: "" },
  });

  // Resend countdown: one self-cancelling timeout per tick (no interval to leak).
  React.useEffect(() => {
    if (resendInSeconds <= 0) return;
    const id = window.setTimeout(() => setResendInSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => window.clearTimeout(id);
  }, [resendInSeconds]);

  const sendMutation = useMutation({
    mutationFn: () =>
      apiFetch<SendResponse>("/auth/mfa/send", {
        method: "POST",
        body: { mfa_token: mfaTokenRef.current },
        skipAuthRefresh: true,
      }),
    onSuccess: (data) => {
      setBanner(null);
      // A code is on its way — reveal the OTP step (no-op if already on it for a resend).
      setStep("otp");
      setResendInSeconds(toCountdownSeconds(data.resend_available_in_s));
    },
    onError: (err) => {
      setBanner(toAuthBanner(err));
      // AUTH-005: the code was very likely already sent, so reveal the OTP step (with
      // the warn banner) and keep the resend control disabled (AC2/AC5).
      if (err instanceof ApiError && err.code === "AUTH-005") {
        setStep("otp");
        setResendInSeconds(RATE_LIMIT_COOLDOWN_S);
      }
      // Any other delivery failure (AUTH-503/network) on the initial send keeps the
      // user on the credentials step (step is unchanged) so they can retry against a
      // visible banner instead of staring at an OTP field with no code en route.
    },
  });

  const loginMutation = useMutation({
    mutationFn: (values: CredentialsValues) =>
      apiFetch<LoginResponse>("/auth/login", {
        method: "POST",
        body: values,
        skipAuthRefresh: true,
      }),
    onSuccess: (data) => {
      mfaTokenRef.current = data.mfa_token;
      setBanner(null);
      setCode("");
      // Login does NOT send the OTP — we request delivery, and the OTP step is
      // revealed only once the code is on its way (sendMutation.onSuccess / AUTH-005).
      // Triggered imperatively (not via an effect) to avoid a StrictMode double-fire.
      sendMutation.mutate();
    },
    onError: (err) => setBanner(toAuthBanner(err)),
    onSettled: () => {
      loginInFlightRef.current = false;
    },
  });

  // verify + token, chained in one mutation so the OTP submit has a single in-flight
  // state and the call order (verify → token) is deterministic.
  const verifyMutation = useMutation({
    mutationFn: async (otp: string) => {
      await apiFetch<{ verified: boolean }>("/auth/mfa/verify", {
        method: "POST",
        body: { mfa_token: mfaTokenRef.current, code: otp },
        skipAuthRefresh: true,
      });
      const token = await apiFetch<TokenResponse>("/auth/token", {
        method: "POST",
        body: { mfa_token: mfaTokenRef.current },
        skipAuthRefresh: true,
      });
      return token.access_token;
    },
    onSuccess: async (accessToken) => {
      setBanner(null);
      await setSession(accessToken); // in-memory only (auth-context) — never persisted
      // Replay the guard-captured deep link only if THIS user's role may reach it.
      // returnTo may have been stashed for a different identity (e.g. an admin who
      // signed out on /admin/users); replaying it blind would land a clinician on
      // /403 (Franklin#317). The auth-context `user` isn't updated in this tick yet,
      // so read the role straight from the just-issued token; when the path isn't
      // permitted, fall back to "/" where HomeRedirect lands by role.
      const role = decodeJwt(accessToken)?.role;
      const target = canRoleAccessPath(pathnameOf(returnTo), role) ? returnTo : "/";
      navigate(target, { replace: true });
    },
    onError: (err) => {
      setBanner(toAuthBanner(err));
      // The verify leg may have already consumed the OTP before a later failure on
      // the token leg, so the entered code is now stale regardless of which step
      // failed. Clear it and re-enable resend so the user can request a fresh code
      // instead of being stuck re-submitting a known-bad / consumed code.
      setCode("");
      setResendInSeconds(0);
    },
  });

  const onCredentialsSubmit = form.handleSubmit((values) => {
    // Synchronous guard (see loginInFlightRef): blocks a same-tick double-submit that
    // the async isPending/disabled flags would miss.
    if (loginInFlightRef.current || loginMutation.isPending || sendMutation.isPending) return;
    loginInFlightRef.current = true;
    setBanner(null);
    loginMutation.mutate(values);
  });

  const onVerifySubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isOtpComplete(code) || verifyMutation.isPending) return;
    setBanner(null);
    verifyMutation.mutate(code);
  };

  const onResend = () => {
    if (resendInSeconds > 0 || sendMutation.isPending) return;
    setBanner(null);
    sendMutation.mutate();
  };

  const onBack = () => {
    // Never leave the OTP step while verify→token OR a resend is in flight: verify's
    // onSuccess would still run setSession + navigate (logging the user in after they
    // returned to credentials), and a resend's onSuccess calls setStep("otp") — which
    // would bounce the user straight back to the OTP step. The Back button is also
    // disabled in these states (belt and braces).
    if (verifyMutation.isPending || sendMutation.isPending) return;
    mfaTokenRef.current = null;
    setCode("");
    setBanner(null);
    setResendInSeconds(0);
    setStep("credentials");
  };

  const loginPending = loginMutation.isPending;
  // While on the credentials step, the initial mfa/send runs before the OTP step is
  // revealed — keep the submit busy and show a "Sending code…" state through it.
  const sendingInitialCode = step === "credentials" && sendMutation.isPending;
  const credentialsBusy = loginPending || sendingInitialCode;

  return (
    <main className="flex min-h-screen items-center justify-center p-5">
      <div className="grid w-full max-w-[min(var(--width-sm),100%)] items-stretch gap-10 lg:max-w-[1120px] lg:grid-cols-[1.05fr_0.95fr]">
        {/* Hero panel — decorative, hidden on small viewports (the auth card is the
            required element; the hero is optional/responsive per the mockup). */}
        <section
          className="hidden flex-col justify-center gap-7 rounded-3xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] p-14 lg:flex"
          aria-hidden="true"
        >
          <div className="flex items-center gap-4">
            <img
              src="/cits-logo.svg"
              alt=""
              width={88}
              height={88}
              className="h-[88px] w-[88px] shrink-0"
            />
            <div className="leading-tight">
              <div className="text-2xl font-bold text-[color:var(--text-primary)]">CITS</div>
              <div className="text-md text-[color:var(--text-tertiary)]">
                Center for Innovative Therapy Solutions
              </div>
            </div>
          </div>
          <p className="display-sm text-[color:var(--text-primary)]">
            AMPS scoring, redesigned for a calmer digital workflow.
          </p>
          <p className="text-md text-[color:var(--text-secondary)]">
            A modern web experience for evaluation entry, results review, and readiness
            management — clear, professional, and easy to trust in daily clinical use.
          </p>
        </section>

        <Card className="w-[min(520px,100%)] justify-self-center p-8">
          <div className="mb-5 flex items-center gap-3 lg:hidden">
            <img
              src="/cits-logo.svg"
              alt=""
              width={40}
              height={40}
              className="h-10 w-10 shrink-0"
            />
            <span className="text-lg font-bold text-[color:var(--text-primary)]">CITS</span>
          </div>
          <CardHeader>
            <CardTitle>Sign in</CardTitle>
            <CardDescription>
              {step === "credentials"
                ? "Access your CITS AMPS workspace securely."
                : "Enter the 6-digit code we emailed you to finish signing in."}
            </CardDescription>
          </CardHeader>

          {registeredNotice ? (
            <Banner variant="success" className="mt-4">
              {registeredNotice}
            </Banner>
          ) : null}

          {banner ? (
            <Banner variant={banner.variant} className="mt-4">
              {banner.message}
            </Banner>
          ) : null}

          {step === "credentials" ? (
            <form key="credentials" className="mt-5 flex flex-col gap-4" onSubmit={onCredentialsSubmit} noValidate>
              <FormField label="Email" error={form.formState.errors.email?.message} required>
                <Input type="email" autoComplete="email" {...form.register("email")} />
              </FormField>
              <FormField label="Password" error={form.formState.errors.password?.message} required>
                <Input type="password" autoComplete="current-password" {...form.register("password")} />
              </FormField>

              {/* One credential submit. Admins sign in here too — role-based routing
                  after /auth/token sends them to the admin console automatically, so
                  there is no separate admin auth path. */}
              <Button type="submit" variant="primary" disabled={credentialsBusy}>
                {loginPending ? "Signing in…" : sendingInitialCode ? "Sending code…" : "Sign in as clinician"}
              </Button>

              <p className="mt-1 text-center text-sm text-[color:var(--text-tertiary)]">
                <Button asChild variant="link">
                  <Link to="/sign-up/legacy">Sign up as a legacy user</Link>
                </Button>
              </p>
            </form>
          ) : (
            <form key="otp" className="mt-5 flex flex-col gap-4" onSubmit={onVerifySubmit} noValidate>
              <FormField
                label="Enter the 6-digit code sent to your email"
                helper="Enter all 6 digits."
                htmlFor="otp"
                required
              >
                <Input
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                />
              </FormField>

              <Button
                type="submit"
                variant="primary"
                disabled={!isOtpComplete(code) || verifyMutation.isPending}
              >
                {verifyMutation.isPending ? "Verifying…" : "Verify and continue"}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onResend}
                disabled={resendInSeconds > 0 || sendMutation.isPending}
              >
                {resendInSeconds > 0 ? `Resend code (${resendInSeconds}s)` : "Resend code"}
              </Button>
              <Button
                type="button"
                variant="link"
                onClick={onBack}
                disabled={verifyMutation.isPending || sendMutation.isPending}
              >
                Back
              </Button>
            </form>
          )}
        </Card>
      </div>
    </main>
  );
}

export default SignInScreen;
